import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { FaSearch } from "react-icons/fa";
import * as XLSX from "xlsx";
import PageHeader1 from "../../components/common/PageHeader1";
import { useDispatch, useSelector } from "react-redux";
import { fetchSessionReport } from "../../Redux/Reducers/InvoiceReportReducer";
import toast from "react-hot-toast";

const SessionReports = () => {
  const dispatch = useDispatch();

  const reportData = useSelector((state) => state?.InvoiceReportReducer || {});
  const { data: rowsData = [], paginationDetail = {}, status = "idle" } = reportData;

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Search and date filter state
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");

  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const handleClickOpen = (id) => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleDelete = () => {
    setOpen(false);
  };

  // Load data on mount and when pagination changes (only if search is not applied)
  useEffect(() => {
    // If search is applied, don't trigger this effect for pagination
    if (appliedSearch) {
      return;
    }
    
    const page = paginationModel.page + 1;
    const limit = paginationModel.pageSize;
    const params = { page, limit };
    
    // Add date filters if applied
    if (appliedStartDate) {
      params.startDate = appliedStartDate;
    }
    if (appliedEndDate) {
      params.endDate = appliedEndDate;
    }
    
    dispatch(fetchSessionReport(params));
  }, [dispatch, paginationModel, appliedStartDate, appliedEndDate, appliedSearch]);

  const handleSearch = () => {
    if (!search.trim()) {
      // If search is empty, reset and load all data
      setAppliedSearch("");
      setPaginationModel({ page: 0, pageSize: 10 });
      dispatch(fetchSessionReport({ page: 1, limit: 10 }));
      return;
    }
    setAppliedSearch(search);
    // Call API with only search parameter (no page/limit)
    dispatch(fetchSessionReport({ search: search }));
  };

  // Handle when search input is cleared
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    
    // If search is cleared (empty), reset and load all data
    if (!value.trim() && appliedSearch) {
      setAppliedSearch("");
      setPaginationModel({ page: 0, pageSize: 10 });
      dispatch(fetchSessionReport({ page: 1, limit: 10 }));
    }
  };

  const handleApplyFilter = () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start date and end date");
      return;
    }
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedSearch(""); // Clear search when applying date filter
    setPaginationModel({ page: 0, pageSize: 10 }); // Reset to first page when applying filter
    const page = 1;
    const limit = 10;
    dispatch(fetchSessionReport({
      page,
      limit,
      startDate: startDate,
      endDate: endDate,
    }));
  };

  const handleClearFilters = () => {
    setSearch("");
    setStartDate("");
    setEndDate("");
    setAppliedSearch("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setPaginationModel({ page: 0, pageSize: 10 });
    dispatch(fetchSessionReport({ page: 1, limit: 10 }));
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  const convertSecondsToTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours} hr ${minutes} min ${seconds} sec`;
};

const formatCamelCase = (str) => {
  if (!str) return '';
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2") 
    .replace(/^./, (char) => char.toUpperCase()); 
};
  // Calculate rowCount: use paginationDetail if available, otherwise use data length (for search)
  const rowCount = appliedSearch 
    ? (rowsData?.length || 0)
    : (paginationDetail?.totalRecords || 0);

  const rows = rowsData?.map((item, index) => ({
    ...item,
    id: item._id || item.id || index,
    sono: paginationModel.page * paginationModel.pageSize + index + 1,
    mobileNumber: item?.mobileNumber,
    astrologerName: item?.astrologerName,
    sessionType: formatCamelCase(item?.sessionType),
    walletBefore: item?.walletBefore?.toFixed(2),
    walletAmount: item?.walletAmount?.toFixed(2),
    walletAfter: item?.walletAfter?.toFixed(2),
    astroShare: item?.astroShare?.toFixed(2),
    promotionShare: item?.promotionShare?.toFixed(2),
    companyShare: item?.companyShare?.toFixed(2),
    duration: convertSecondsToTime(item?.duration),
    createdAt: item.createdAt ? new Date(item.createdAt).toLocaleString()
    : "",
updatedAt: item.updatedAt ? new Date(item.updatedAt).toLocaleString()
    : "",
    
  }));

  const columns = [
    { field: "sono", headerName: "S.No", width: 80 },
    { field: "astrologerName", headerName: "Astrologer Name", width: 160 },
    { field: "userName", headerName: "User Name", width: 140 },
    { field: "mobileNumber", headerName: "Mobile No.", width: 130 },
    { field: "sessionType", headerName: "Call/Chat", width: 110 },
    { field: "session", headerName: "Session", width: 150 },
    { field: "duration", headerName: "Duration", width: 100 },
    { field: "walletBefore", headerName: "Wallet Before", width: 140 },
    { field: "walletAmount", headerName: "Wallet During Consult", width: 180 },
    { field: "walletAfter", headerName: "Wallet After", width: 140 },
    // { field: "totalAmount", headerName: "Total Amount", width: 130 },
    { field: "astroShare", headerName: "Astro Share", width: 130 },
    { field: "promotionShare", headerName: "Promotion Share", width: 150 },
    { field: "companyShare", headerName: "Company Share", width: 150 },
    { field: "createdAt", headerName: "Created At", width: 170 },
    { field: "updatedAt", headerName: "Updated At", width: 170 },
  ];

  const exportToExcel = () => {
    if (!Array.isArray(rows) || rows.length === 0) return;

    const exportData = rows.map((row) => ({
      SNo: row.sono,
      UserName: row.userName,
      MobileNumber: row.mobileNumber,
      AstrologerName: row.astrologerName,
      ConversationType: row.callOrChat,
      Session: row.session,
      Duration: row.duration,
      WalletBefore: row.walletBefore,
      WalletDuring: row.walletDuring,
      WalletAfter: row.walletAfter,
      AstroShare: row.astroShare,
      PromotionShare: row.promotionShare,
      CompanyShare: row.companyShare,
      
    

    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "SessionReports");
    XLSX.writeFile(workbook, `Session_Report_${new Date().toISOString()}.xlsx`);
  };

  return (
    <div className="body d-flex py-1">
      <div className="container-xxl">
        <PageHeader1 pagetitle="Session Reports" />
        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <Box sx={{ width: "100%" }}>
                  {/* Search and Date Filter Section - Inside table card, single line */}
                  <Box display="flex" alignItems="flex-end" gap={2} mb={2} flexWrap="wrap">
                    <TextField
                      placeholder="Search..."
                      value={search}
                      onChange={handleSearchChange}
                      size="small"
                      sx={{ width: '250px' }}
                    />
                    <IconButton
                      onClick={handleSearch}
                      disabled={status === "loading" || !search.trim()}
                      sx={{ 
                        height: '40px',
                        width: '40px',
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                        '&:disabled': {
                          backgroundColor: 'action.disabledBackground',
                          color: 'action.disabled',
                        }
                      }}
                    >
                      <FaSearch />
                    </IconButton>
                    <TextField
                      type="date"
                      label="Start Date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ width: '180px' }}
                    />
                    <TextField
                      type="date"
                      label="End Date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ width: '180px' }}
                    />
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={handleApplyFilter}
                      disabled={status === "loading"}
                      sx={{ height: '40px' }}
                    >
                      {status === "loading" ? "Loading..." : "Apply"}
                    </Button>
                    <Button 
                      variant="outlined" 
                      color="secondary" 
                      onClick={handleClearFilters}
                      sx={{ height: '40px' }}
                    >
                      Clear
                    </Button>
                  </Box>
                  
                  <Box sx={{ height: 600 }}>
                    <DataGrid
                      columns={columns}
                      rows={rows}
                      getRowId={(row) => row.id}
                      rowCount={rowCount}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                      pageSizeOptions={[10, 25, 50, 100]}
                      paginationMode={appliedSearch ? "client" : "server"}
                      loading={status === "loading"}
                      disableSelectionOnClick
                    />
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this session record?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SessionReports;
